/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Cài đặt › E-mail & thông báo › Zalo ZNS, the branch page of the notify module `zalo-zns`: which
 * notice goes to Zalo with which Zalo template (and how each parameter is filled), "Gửi thử", the
 * messages sent and numbers that receive no Zalo message.
 * The connection itself is the module's instance in Cài đặt › Module › Thông báo.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { api } from '../../api';
import { isApiError } from '../../api/client';
import {
  ZNS_MODULE,
  type ProviderTemplate,
  type ZnsEmailMode,
  type ZnsMappingError,
  type ZnsMessageRow,
  type ZnsMessageStatus,
  type ZnsOverview,
  type ZnsParamValue,
  type ZnsTemplateItem,
  type ZnsTestResult,
} from '../../api/areas/zns';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../components/blocks';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { modulesUrl } from '../modules/ui';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

export const ZNS_STATUSES: ZnsMessageStatus[] = ['PENDING', 'SENDING', 'SENT', 'DELIVERED', 'FAILED', 'UNKNOWN', 'SUPPRESSED'];
const STATUS_TONE: Record<ZnsMessageStatus, Tone> = {
  PENDING: 'pending',
  SENDING: 'pending',
  SENT: 'info',
  DELIVERED: 'ok',
  FAILED: 'crit',
  UNKNOWN: 'warn',
  SUPPRESSED: 'neutral',
};

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

/** The label key of a stored code: the notify branch's and the token chain's codes as they are
 * (`REFRESH_REFUSED:-14014` → `REFRESH_REFUSED`), Zalo's own (`-108`) under `zaloCode`, an HTTP
 * error without a Zalo code (`HTTP_404`) as `HTTP`. */
export function codeKey(code: string): string {
  if (code.startsWith('REFRESH_REFUSED')) return 'REFRESH_REFUSED';
  if (code.startsWith('HTTP_')) return 'HTTP';
  return code;
}

function useZns() {
  const { t } = useTranslation('zns');
  const { t: ti } = useTranslation('integrations');
  const tr = t as unknown as Lookup;
  const notice = (key: string) => (ti as unknown as Lookup)(`email.keys.${key}`, { defaultValue: key });
  /** A code of the notify branch, the core or the token chain. */
  const code = (c: string | null | undefined) => (c ? tr(`code.${codeKey(c)}`, { defaultValue: c }) : '');
  /** Zalo's own code, with its number. */
  const zaloCode = (c: string | null | undefined) => (c ? `${tr(`zaloCode.${codeKey(c)}`, { defaultValue: tr('zaloCode.HTTP') })} (${c})` : '');
  return { t, tr, notice, code, zaloCode };
}

/** Where the connection is configured: the module's instance, or the branch to add one. */
function connectionUrl(configured: boolean): string {
  return configured ? modulesUrl('notify', ZNS_MODULE, 'default') : modulesUrl('notify');
}

/** Tình trạng: on/off, the token chain, quota, last problem, counts. */
export function ZnsStatus({ overview, withLink }: { overview: ZnsOverview; withLink?: boolean }) {
  const { t, tr, code, zaloCode } = useZns();
  const c = overview.connection?.chain ?? null;
  const conn = overview.connection;
  return (
    <div className="grid gap-3 text-sm">
      {!overview.configured ? <Callout tone="warn">{t('overview.notConfigured')}</Callout> : null}
      {overview.configured && !overview.enabled ? <Callout tone="warn">{t('overview.off')}</Callout> : null}
      {overview.development ? <Callout tone="info">{t('overview.development')}</Callout> : null}
      {overview.enabled ? (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
          <dt className="text-muted-foreground">{t('overview.title')}</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <Badge tone="ok">{t('overview.on', { version: overview.version ?? '—' })}</Badge>
            {c ? <Badge tone={c.state === 'READY' ? 'ok' : 'crit'}>{tr(`state.${c.state}`)}</Badge> : null}
            {overview.health && overview.health.state !== 'healthy' ? (
              <Badge tone={overview.health.state === 'down' ? 'crit' : overview.health.state === 'degraded' ? 'warn' : 'neutral'}>{tr(`health.${overview.health.state}`)}</Badge>
            ) : null}
          </dd>
          {conn && !c ? (
            <>
              <dt className="text-muted-foreground">{t('overview.chain')}</dt>
              <dd>{t('overview.noChain')}</dd>
            </>
          ) : null}
          {conn && c && !conn.chain_is_this_version ? (
            <>
              <dt className="text-muted-foreground">{t('overview.chain')}</dt>
              <dd>{t('overview.previousChain')}</dd>
            </>
          ) : null}
          {c ? (
            <>
              <dt className="text-muted-foreground">{t('overview.accessUntil')}</dt>
              <dd>{c.access_expires_at ? formatDateTime(c.access_expires_at) : '—'}</dd>
              <dt className="text-muted-foreground">{t('overview.renewed')}</dt>
              <dd>
                {c.refreshed_at ? formatDateTime(c.refreshed_at) : '—'} · {t('overview.renewals')}: {c.refresh_count}
              </dd>
              {c.last_error_code ? (
                <>
                  <dt className="text-muted-foreground">{t('overview.lastProblem')}</dt>
                  <dd>
                    {code(c.last_error_code)}
                    {c.last_error_at ? <span className="text-muted-foreground"> · {formatDateTime(c.last_error_at)}</span> : null}
                  </dd>
                </>
              ) : null}
              {c.next_refresh_at ? (
                <>
                  <dt className="text-muted-foreground">{t('overview.nextTry')}</dt>
                  <dd>{formatDateTime(c.next_refresh_at)}</dd>
                </>
              ) : null}
            </>
          ) : null}
          {conn ? (
            <>
              <dt className="text-muted-foreground">{t('overview.quota')}</dt>
              <dd>
                {conn.quota_remaining !== null && conn.quota_daily !== null ? t('overview.quotaValue', { remaining: conn.quota_remaining, daily: conn.quota_daily }) : '—'}
                {conn.quota_at ? <span className="text-muted-foreground"> ({t('overview.quotaAt', { at: formatDateTime(conn.quota_at) })})</span> : null}
              </dd>
              {conn.last_problem_code ? (
                <>
                  <dt className="text-muted-foreground">{t('overview.lastConnectionProblem')}</dt>
                  <dd>
                    {zaloCode(conn.last_problem_code)}
                    {conn.last_problem_at ? <span className="text-muted-foreground"> · {formatDateTime(conn.last_problem_at)}</span> : null}
                  </dd>
                </>
              ) : null}
            </>
          ) : null}
          <dt className="text-muted-foreground">{t('overview.counts')}</dt>
          <dd className="flex flex-wrap gap-1">
            {ZNS_STATUSES.filter((s) => overview.counts_7d[s]).map((s) => (
              <Badge key={s} tone={STATUS_TONE[s]}>
                {tr(`status.${s}`)}: {overview.counts_7d[s]}
              </Badge>
            ))}
          </dd>
        </dl>
      ) : null}
      {overview.enabled ? <p className="text-xs text-muted-foreground">{t('overview.fallbackMinutes', { minutes: overview.email_fallback_minutes })}</p> : null}
      {withLink ? (
        <div>
          <Button asChild variant="outline" size="sm">
            <Link to={connectionUrl(overview.configured)}>{t('overview.openConnection')}</Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template registry

interface ParamRow {
  name: string;
  /** A source, or `text`. */
  kind: string;
  text: string;
}

function rowsOf(params: Record<string, ZnsParamValue>, zalo: ProviderTemplate | null): ParamRow[] {
  const rows: ParamRow[] = Object.entries(params).map(([name, v]) => ('source' in v ? { name, kind: v.source, text: '' } : { name, kind: 'text', text: v.text }));
  for (const p of zalo?.params ?? []) if (!rows.some((r) => r.name === p.name)) rows.push({ name: p.name, kind: '', text: '' });
  return rows;
}

function paramsOf(rows: ParamRow[]): Record<string, ZnsParamValue> {
  const out: Record<string, ZnsParamValue> = {};
  for (const r of rows) {
    const name = r.name.trim();
    if (!name) continue;
    out[name] = r.kind === 'text' ? { text: r.text } : { source: r.kind };
  }
  return out;
}

function mappingErrors(error: unknown): ZnsMappingError[] {
  if (!isApiError(error) || error.code !== 'MAPPING_INVALID') return [];
  const d = error.details as { errors?: ZnsMappingError[] } | null;
  return Array.isArray(d?.errors) ? d.errors : [];
}

function MappingDialog({ item, onClose, onSaved }: { item: ZnsTemplateItem; onClose: () => void; onSaved: () => void }) {
  const { t, tr, notice, code, zaloCode } = useZns();
  const m = item.mapping;
  const [enabled, setEnabled] = useState(m?.enabled ?? true);
  const [templateId, setTemplateId] = useState(m?.provider_template_id ?? '');
  const [mode, setMode] = useState<ZnsEmailMode>(m?.email_mode ?? 'ALSO');
  const [zalo, setZalo] = useState<ProviderTemplate | null>(m?.provider_template ?? null);
  const [rows, setRows] = useState<ParamRow[]>(() => rowsOf(m?.params ?? {}, m?.provider_template ?? null));
  const [phone, setPhone] = useState('');
  const [test, setTest] = useState<ZnsTestResult | null>(null);
  const save = useWrite((body: Parameters<typeof api.saveZnsTemplate>[1], opts) => api.saveZnsTemplate(item.key, body, opts));
  const remove = useWrite((_: null, opts) => api.deleteZnsTemplate(item.key, opts));
  const load = useWrite((id: string, opts) => api.describeZnsTemplate(item.key, { provider_template_id: id }, opts));
  const send = useWrite((body: Parameters<typeof api.testZnsTemplate>[1], opts) => api.testZnsTemplate(item.key, body, opts));
  const errors = save.state.phase === 'error' ? mappingErrors(save.state.error) : send.state.phase === 'error' ? mappingErrors(send.state.error) : [];
  const errorFor = (name: string) => errors.filter((e) => e.param === name).map((e) => tr(`mappingError.${e.code}`, { defaultValue: e.code }));
  const general = errors.filter((e) => e.param === '' || !rows.some((r) => r.name.trim() === e.param));
  const zp = (name: string) => zalo?.params.find((p) => p.name === name);
  const setRow = (i: number, patch: Partial<ParamRow>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="grid gap-4 text-sm">
      <label className="flex items-center gap-2">
        <Switch checked={enabled} onCheckedChange={(v) => setEnabled(v)} />
        {t('templates.enabled')}
      </label>
      <div className="flex flex-wrap items-end gap-2">
        <Field label={t('templates.templateId')} hint={t('templates.templateIdHint')}>
          <Input value={templateId} onChange={(e) => setTemplateId(e.target.value)} placeholder="295958" />
        </Field>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={load.busy || !templateId.trim()}
          onClick={() =>
            void load.run(templateId.trim()).then((r) => {
              if (r) {
                setZalo(r.template);
                setRows((rs) => rowsOf(paramsOf(rs), r.template));
              }
            })
          }
        >
          {t('templates.load')}
        </Button>
      </div>
      {load.state.phase === 'error' ? <WriteError error={load.state.error} /> : null}
      {zalo ? (
        <p className="text-xs text-muted-foreground">
          {t('templates.loaded', {
            name: zalo.name ?? zalo.provider_template_id,
            status: zalo.status ? tr(`zaloStatus.${zalo.status}`, { defaultValue: zalo.status }) : '—',
            tag: zalo.tag ? tr(`zaloTag.${zalo.tag}`, { defaultValue: zalo.tag }) : '—',
            price: zalo.price ? t('templates.price', { price: zalo.price }) : '',
          })}
        </p>
      ) : null}
      {zalo && zalo.usable === false ? <Callout tone="warn">{t('templates.notUsable')}</Callout> : null}
      <Field label={t('templates.params')}>
        <div className="grid gap-2">
          {rows.map((r, i) => {
            const z = zp(r.name);
            const errs = errorFor(r.name.trim());
            return (
              // eslint-disable-next-line react/no-array-index-key -- rows have no identity besides their position while being edited
              <div key={i} className="grid gap-1 rounded border p-2">
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <Input aria-label={t('templates.paramName')} placeholder={t('templates.paramName')} value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} />
                  <select aria-label={t('templates.paramValue')} className={selectClass} value={r.kind} onChange={(e) => setRow(i, { kind: e.target.value })}>
                    <option value="">—</option>
                    {item.sources.map((s) => (
                      <option key={s} value={s}>
                        {tr(`source.${s}`, { defaultValue: s })}
                      </option>
                    ))}
                    <option value="text">{t('source.text')}</option>
                  </select>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                    {t('templates.remove')}
                  </Button>
                </div>
                {r.kind === 'text' ? <Input aria-label={t('templates.fixedText')} placeholder={t('templates.fixedText')} value={r.text} onChange={(e) => setRow(i, { text: e.target.value })} /> : null}
                {z ? (
                  <p className="text-xs text-muted-foreground">
                    {z.required ? t('templates.required') : null}
                    {z.required && z.max_len ? ' · ' : null}
                    {z.max_len ? t('templates.maxLength', { n: z.max_len }) : null}
                  </p>
                ) : null}
                {errs.map((e) => (
                  <p key={e} className="text-xs text-critical">
                    {e}
                  </p>
                ))}
              </div>
            );
          })}
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, { name: '', kind: '', text: '' }])}>
              {t('templates.addParam')}
            </Button>
          </div>
        </div>
      </Field>
      <Field label={t('templates.mode')} hint={item.fallback_allowed ? t(`emailModeHint.${mode}`) : t('emailModeHint.onlyAlso')}>
        <select className={selectClass} value={mode} onChange={(e) => setMode(e.target.value as ZnsEmailMode)} disabled={!item.fallback_allowed}>
          <option value="ALSO">{t('emailMode.ALSO')}</option>
          {item.fallback_allowed ? <option value="FALLBACK">{t('emailMode.FALLBACK')}</option> : null}
        </select>
      </Field>
      {general.length > 0 ? (
        <Callout tone="warn">
          {general.map((e) => (
            <span key={`${e.param}:${e.code}`} className="block">
              {e.param ? `${e.param}: ` : ''}
              {tr(`mappingError.${e.code}`, { defaultValue: e.code })}
            </span>
          ))}
        </Callout>
      ) : null}
      {save.state.phase === 'error' && errors.length === 0 ? <WriteError error={save.state.error} /> : null}
      {save.state.phase === 'done' ? <Callout tone="ok">{t('templates.saved')}</Callout> : null}
      {remove.state.phase === 'done' ? <Callout tone="ok">{t('templates.deleted')}</Callout> : null}
      {remove.state.phase === 'error' ? <WriteError error={remove.state.error} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          disabled={save.busy}
          onClick={() =>
            void save
              .run({ enabled, provider_template_id: templateId.trim(), params: paramsOf(rows), email_mode: mode, expected_version: m?.version })
              .then((r) => {
                if (r) onSaved();
              })
          }
        >
          {t('templates.save')}
        </Button>
        {m ? (
          <Button
            type="button"
            variant="outline"
            disabled={remove.busy}
            onClick={() =>
              void remove.run(null).then((r) => {
                if (r) {
                  onSaved();
                  onClose();
                }
              })
            }
          >
            {t('templates.delete')}
          </Button>
        ) : null}
      </div>
      <Section title={t('test.title')} description={t('test.sample')}>
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('test.phone')} hint={t('test.phoneHint')}>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0912 345 678" inputMode="tel" />
          </Field>
          <Button
            type="button"
            variant="outline"
            disabled={send.busy || !phone.trim() || !templateId.trim()}
            onClick={() =>
              void send.run({ phone: phone.trim(), draft: { provider_template_id: templateId.trim(), params: paramsOf(rows) } }).then((r) => {
                if (r) setTest(r);
              })
            }
          >
            {t('test.send')}
          </Button>
        </div>
        {send.state.phase === 'error' && errors.length === 0 ? <WriteError error={send.state.error} /> : null}
        {test ? (
          <p className="mt-2">
            <Badge tone={STATUS_TONE[test.status]}>{t('test.result', { status: tr(`status.${test.status}`) })}</Badge>
            {test.error_code ? <span className="ml-2">{code(test.error_code)}</span> : null}
            {test.provider_code ? <span className="ml-2 text-muted-foreground">{zaloCode(test.provider_code)}</span> : null}
          </p>
        ) : null}
      </Section>
      <p className="text-xs text-muted-foreground">{notice(item.key)}</p>
    </div>
  );
}

function TemplatesSection() {
  const { t, notice } = useZns();
  const r = useApi((signal) => api.listZnsTemplates({ signal }), []);
  const [open, setOpen] = useState<ZnsTemplateItem | null>(null);
  const columns: Column<ZnsTemplateItem>[] = [
    { key: 'notice', header: t('templates.notice'), className: 'whitespace-normal', cell: (i) => notice(i.key) },
    {
      key: 'template',
      header: t('templates.template'),
      className: 'whitespace-normal',
      cell: (i) =>
        i.mapping ? (
          <span>
            {i.mapping.provider_template_id}
            {i.mapping.provider_template?.name ? <span className="block text-xs text-muted-foreground">{i.mapping.provider_template.name}</span> : null}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('templates.none')}</span>
        ),
    },
    { key: 'mode', header: t('templates.mode'), className: 'whitespace-normal', cell: (i) => (i.mapping ? t(`emailMode.${i.mapping.email_mode}`) : '—') },
    {
      key: 'on',
      header: '',
      cell: (i) => (i.mapping ? <Badge tone={i.mapping.enabled ? 'ok' : 'neutral'}>{i.mapping.enabled ? t('templates.on') : t('templates.off')}</Badge> : null),
    },
    {
      key: 'edit',
      header: '',
      cell: (i) => (
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(i)}>
          {t('templates.edit')}
        </Button>
      ),
    },
  ];
  return (
    <Section title={t('templates.title')} description={t('templates.intro')} flush>
      {r.status === 'loading' ? (
        <div className="px-5">
          <Loading rows={4} />
        </div>
      ) : null}
      {r.status === 'error' ? (
        <div className="px-5 pb-5">
          <WriteError error={r.error} />
        </div>
      ) : null}
      {r.status === 'success' ? (
        <>
          <DataTable columns={columns} rows={r.data.items} rowKey={(i) => i.key} />
          <p className="px-5 py-3 text-xs text-muted-foreground">{t('templates.never', { list: r.data.never.map(notice).join(', ') })}</p>
        </>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{open ? t('templates.dialogTitle', { name: notice(open.key) }) : ''}</DialogTitle>
            <DialogDescription>{t('templates.dialogDescription')}</DialogDescription>
          </DialogHeader>
          {open ? <MappingDialog key={open.key} item={open} onClose={() => setOpen(null)} onSaved={r.reload} /> : null}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Message log

function MessageDetail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const { t, tr, notice, code, zaloCode } = useZns();
  const { t: ti } = useTranslation('integrations');
  const r = useApi((signal) => api.getZnsMessage(id, { signal }), [id]);
  const resend = useWrite((_: null, opts) => api.resendZnsMessage(id, opts));
  if (r.status === 'loading') return <Loading rows={3} />;
  if (r.status === 'error') return <WriteError error={r.error} />;
  const m = r.data;
  return (
    <div className="grid gap-3 text-sm">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">{t('log.status')}</dt>
        <dd className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONE[m.status]}>{tr(`status.${m.status}`)}</Badge>
          {m.error_code ? <span>{code(m.error_code)}</span> : null}
        </dd>
        {m.provider_code ? (
          <>
            <dt className="text-muted-foreground">{t('log.zaloAnswer')}</dt>
            <dd>{zaloCode(m.provider_code)}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">{t('log.notice')}</dt>
        <dd>
          {notice(m.template_key)} · {tr(`purpose.${m.purpose}`)}
          {m.domain ? <span className="block text-xs text-muted-foreground">{m.domain}</span> : null}
        </dd>
        <dt className="text-muted-foreground">{t('log.phone')}</dt>
        <dd>{m.phone}</dd>
        <dt className="text-muted-foreground">{t('log.template')}</dt>
        <dd>{m.provider_template_id}</dd>
        <dt className="text-muted-foreground">{t('log.tracking')}</dt>
        <dd className="break-all font-mono text-xs">{m.tracking_id}</dd>
        {m.provider_message_id ? (
          <>
            <dt className="text-muted-foreground">{t('log.zaloId')}</dt>
            <dd className="break-all font-mono text-xs">{m.provider_message_id}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">{t('log.attempts')}</dt>
        <dd>
          {m.attempts} / {m.max_attempts}
        </dd>
        <dt className="text-muted-foreground">{t('log.sent')}</dt>
        <dd>{m.sent_at ? formatDateTime(m.sent_at) : '—'}</dd>
        <dt className="text-muted-foreground">{t('log.delivered')}</dt>
        <dd>{m.delivered_at ? formatDateTime(m.delivered_at) : '—'}</dd>
        {m.notification_status ? (
          <>
            <dt className="text-muted-foreground">{t('log.emailStatus')}</dt>
            <dd>{(ti as unknown as Lookup)(`email.log.statuses.${m.notification_status}`, { defaultValue: m.notification_status })}</dd>
          </>
        ) : null}
        {m.test_mode ? (
          <>
            <dt className="text-muted-foreground" />
            <dd>{t('log.development')}</dd>
          </>
        ) : null}
      </dl>
      <Field label={t('log.params')}>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded border bg-muted/40 p-3">
          {Object.entries(m.template_data).map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="font-mono text-xs text-muted-foreground">{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </Field>
      {m.status === 'UNKNOWN' ? <Callout tone="warn">{t('log.resendUnknownHint')}</Callout> : null}
      {resend.state.phase === 'error' ? <WriteError error={resend.state.error} /> : null}
      {resend.state.phase === 'done' ? <Callout tone="ok">{t('log.requeued')}</Callout> : null}
      {m.purpose === 'NOTIFICATION' && (m.status === 'FAILED' || m.status === 'UNKNOWN' || m.status === 'SUPPRESSED') ? (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={resend.busy || resend.state.phase === 'done'}
            onClick={() =>
              void resend.run(null).then((x) => {
                if (x) {
                  r.reload();
                  onChanged();
                }
              })
            }
          >
            {t('log.resend')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function MessagesSection() {
  const { t, tr, notice, code } = useZns();
  const [status, setStatus] = useState<ZnsMessageStatus | ''>('');
  const [phone, setPhone] = useState('');
  const [applied, setApplied] = useState('');
  const [before, setBefore] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<string | null>(null);
  const r = useApi((signal) => api.listZnsMessages({ status: status || undefined, phone: applied || undefined, before, limit: 50 }, { signal }), [status, applied, before]);
  const columns: Column<ZnsMessageRow>[] = [
    { key: 'created', header: t('log.created'), className: 'whitespace-nowrap', cell: (m) => formatDateTime(m.created_at) },
    {
      key: 'notice',
      header: t('log.notice'),
      className: 'whitespace-normal',
      cell: (m) => (
        <button type="button" className="text-left text-primary hover:underline" onClick={() => setOpen(m.id)}>
          {notice(m.template_key)}
          {m.purpose === 'TEST' ? ` · ${tr('purpose.TEST')}` : ''}
          {m.domain ? <span className="block text-xs text-muted-foreground">{m.domain}</span> : null}
        </button>
      ),
    },
    { key: 'phone', header: t('log.phone'), cell: (m) => m.phone },
    {
      key: 'status',
      header: t('log.status'),
      cell: (m) => (
        <div className="flex flex-col items-start gap-1">
          <Badge tone={STATUS_TONE[m.status]}>{tr(`status.${m.status}`)}</Badge>
          {m.error_code ? <span className="max-w-64 truncate text-xs text-muted-foreground">{code(m.error_code)}</span> : null}
        </div>
      ),
    },
  ];
  return (
    <Section title={t('log.title')} flush>
      <div className="px-5">
        <FilterBar>
          <Field label={t('log.status')}>
            <select
              className={selectClass}
              value={status}
              onChange={(e) => {
                setBefore(undefined);
                setStatus(e.target.value as ZnsMessageStatus | '');
              }}
            >
              <option value="">{t('log.any')}</option>
              {ZNS_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {tr(`status.${s}`)}
                </option>
              ))}
            </select>
          </Field>
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setBefore(undefined);
              setApplied(phone.trim());
            }}
          >
            <Field label={t('log.phone')}>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0912…" inputMode="tel" />
            </Field>
            <Button type="submit" variant="outline" size="sm">
              {t('log.search')}
            </Button>
          </form>
        </FilterBar>
      </div>
      {r.status === 'loading' ? (
        <div className="px-5">
          <Loading rows={4} />
        </div>
      ) : null}
      {r.status === 'error' ? (
        <div className="px-5 pb-5">
          <WriteError error={r.error} />
        </div>
      ) : null}
      {r.status === 'success' ? (
        <>
          <DataTable columns={columns} rows={r.data.items} rowKey={(m) => m.id} empty={<EmptyState title={t('log.empty')} />} />
          <div className="flex justify-end gap-2 px-5 py-3">
            {before ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setBefore(undefined)}>
                {t('log.first')}
              </Button>
            ) : null}
            {r.data.next_before ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setBefore(r.data.next_before ?? undefined)}>
                {t('log.older')}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{t('log.detailTitle')}</DialogTitle>
            <DialogDescription>{t('log.detailDescription')}</DialogDescription>
          </DialogHeader>
          {open ? <MessageDetail id={open} onChanged={r.reload} /> : null}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Opt-outs

function OptOutsSection() {
  const { t, tr } = useZns();
  const r = useApi((signal) => api.listZnsOptOuts({}, { signal }), []);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const add = useWrite((body: { phone: string; reason?: string }, opts) => api.addZnsOptOut(body, opts));
  const withdraw = useWrite((id: string, opts) => api.withdrawZnsOptOut(id, opts));
  return (
    <Section title={t('optOuts.title')} description={t('optOuts.intro')}>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void add.run({ phone: phone.trim(), reason: reason.trim() || undefined }).then((x) => {
            if (x) {
              setPhone('');
              setReason('');
              r.reload();
            }
          });
        }}
      >
        <Field label={t('optOuts.phone')}>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="0912 345 678" />
        </Field>
        <Field label={t('optOuts.reason')}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Button type="submit" variant="outline" size="sm" disabled={add.busy || !phone.trim()}>
          {t('optOuts.add')}
        </Button>
      </form>
      {add.state.phase === 'error' ? <WriteError error={add.state.error} /> : null}
      {add.state.phase === 'done' && !add.state.result.added ? <Callout tone="info">{t('optOuts.already')}</Callout> : null}
      {withdraw.state.phase === 'error' ? <WriteError error={withdraw.state.error} /> : null}
      <div className="mt-3">
        {r.status === 'loading' ? <Loading rows={2} /> : null}
        {r.status === 'error' ? <WriteError error={r.error} /> : null}
        {r.status === 'success' && r.data.items.length === 0 ? <EmptyState title={t('optOuts.empty')} /> : null}
        {r.status === 'success' && r.data.items.length > 0 ? (
          <ul className="grid gap-2">
            {r.data.items.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-sm">
                <span>
                  <span className="font-medium">{o.phone}</span>
                  <span className="text-muted-foreground">
                    {' '}
                    · {tr(`optOuts.sourceName.${o.source}`)}
                    {o.module === null ? ` · ${t('optOuts.everyChannel')}` : ''} · {formatDateTime(o.created_at)}
                    {o.reason ? ` · ${o.reason}` : ''}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={withdraw.busy}
                  onClick={() =>
                    void withdraw.run(o.id).then((x) => {
                      if (x) r.reload();
                    })
                  }
                >
                  {t('optOuts.withdraw')}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Section>
  );
}

export function ZaloPage() {
  const { t } = useZns();
  useDocumentTitle(t('page.title'));
  const overview = useApi((signal) => api.getZnsOverview({ signal }), []);
  return (
    <>
      <PageHeader title={t('page.title')} subtitle={t('page.subtitle')} crumbs={[{ label: t('page.crumb') }]} />
      <div className="grid gap-6">
        <Section title={t('overview.title')}>
          {overview.status === 'loading' ? <Loading rows={2} /> : null}
          {overview.status === 'error' ? <WriteError error={overview.error} /> : null}
          {overview.status === 'success' ? <ZnsStatus overview={overview.data} withLink /> : null}
        </Section>
        <TemplatesSection />
        <MessagesSection />
        <OptOutsSection />
      </div>
    </>
  );
}
